import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'

interface LowBandwidthContextType {
  isLowBandwidth: boolean
  setIsLowBandwidth: (v: boolean) => void
}

const LowBandwidthContext = createContext<LowBandwidthContextType | null>(null)

export const LowBandwidthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isLowBandwidth, setIsLowBandwidth] = useState(false)

  // NEW: Monitor network changes
  useEffect(() => {
    const updateBandwidth = () => {
      // Check navigator.connection if available (some browsers support it)
      const connection = (navigator as any).connection
      if (connection) {
        // EffectiveType could be 'slow-2g', '2g', '3g', '4g'
        setIsLowBandwidth(['slow-2g', '2g'].includes(connection.effectiveType))
      } else {
        // fallback: assume good network if not supported
        setIsLowBandwidth(false)
      }
    }

    updateBandwidth() // initial check
    window.addEventListener('online', updateBandwidth)
    window.addEventListener('offline', () => setIsLowBandwidth(true))

    // Optional: listen to changes in navigator.connection (if supported)
    const connection = (navigator as any).connection
    connection?.addEventListener?.('change', updateBandwidth)

    return () => {
      window.removeEventListener('online', updateBandwidth)
      window.removeEventListener('offline', () => setIsLowBandwidth(true))
      connection?.removeEventListener?.('change', updateBandwidth)
    }
  }, [])

  return (
    <LowBandwidthContext.Provider value={{ isLowBandwidth, setIsLowBandwidth }}>
      {children}
    </LowBandwidthContext.Provider>
  )
}

export const useLowBandwidth = () => {
  const ctx = useContext(LowBandwidthContext)
  if (!ctx)
    throw new Error('useLowBandwidth must be inside LowBandwidthProvider')
  return ctx
}
